import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const empty = { name: "", description: "", duration: "", price: 0, free: false };
const fmtPrice = (p) => (p === 0 ? "Gratuita" : `${p}€`);

export default function ServicesManager() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/services").then((r) => { setServices(r.data); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s.id);
    setForm({ name: s.name, description: s.description, duration: s.duration, price: s.price, free: s.free });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Indique o nome do serviço."); return; }
    setSaving(true);
    const body = { ...form, price: form.free ? 0 : parseFloat(form.price) || 0 };
    try {
      if (editing) await api.put(`/admin/services/${editing}`, body);
      else await api.post("/admin/services", body);
      toast.success("Serviço guardado.");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao guardar.");
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este serviço?")) return;
    try { await api.delete(`/admin/services/${id}`); toast.success("Serviço removido."); load(); }
    catch { toast.error("Não foi possível remover."); }
  };

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-rose-700" /></div>;

  return (
    <div data-testid="services-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl text-stone-900">Serviços e preços</h2>
          <p className="text-sm text-stone-500">Adicione, edite ou remova consultas. Os preços atualizam o pagamento automaticamente.</p>
        </div>
        <button onClick={openNew} data-testid="add-service"
          className="btn-rose inline-flex items-center gap-2 rounded-full bg-rose-800 text-white px-5 py-2.5 text-sm">
          <Plus className="w-4 h-4" /> Novo serviço
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {services.map((s) => (
          <div key={s.id} className="bg-white rounded-2xl border border-rose-100 p-6" data-testid={`service-item-${s.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-xl text-stone-900">{s.name}</h3>
                <span className="inline-flex items-center gap-1.5 text-xs text-stone-400 mt-1">
                  <Clock className="w-3.5 h-3.5" /> {s.duration || "—"}
                </span>
              </div>
              <span className="font-display text-2xl text-rose-800">{fmtPrice(s.price)}</span>
            </div>
            <p className="text-sm text-stone-500 mt-3">{s.description}</p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}
                className="inline-flex items-center gap-1.5 text-xs rounded-full border border-rose-200 text-rose-700 px-3 py-1.5 hover:bg-rose-50">
                <Pencil className="w-3.5 h-3.5" /> Editar
              </button>
              <button onClick={() => remove(s.id)} data-testid={`delete-service-${s.id}`}
                className="inline-flex items-center gap-1.5 text-xs rounded-full border border-stone-200 text-stone-500 px-3 py-1.5 hover:bg-stone-50">
                <Trash2 className="w-3.5 h-3.5" /> Remover
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Editar serviço" : "Novo serviço"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="s-name">Nome</Label>
              <Input id="s-name" data-testid="service-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="s-desc">Descrição</Label>
              <Textarea id="s-desc" data-testid="service-description" rows={3} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="s-dur">Duração</Label>
                <Input id="s-dur" data-testid="service-duration" value={form.duration}
                  onChange={(e) => setForm({ ...form, duration: e.target.value })} className="mt-2" placeholder="ex.: 50 min" />
              </div>
              <div>
                <Label htmlFor="s-price">Preço (€)</Label>
                <Input id="s-price" data-testid="service-price" type="number" min="0" step="1"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-2" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} disabled={saving} data-testid="save-service"
              className="btn-rose inline-flex items-center justify-center gap-2 rounded-full bg-rose-800 text-white px-6 py-3 text-sm w-full disabled:opacity-50">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
